import React, { useContext, useMemo } from "react";
import { styled, Autocomplete, TextField, Typography, Stack, Box, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SettingsOptionsContext } from "../SettingsOptions";
import { OptionsValueType } from "./SettingsSingleSelect";

export type WidgetTitleType = {
  type: string;
  value?: string;
};

interface SettingsWidgetTitleSelectProps {

  // Acts as the identifier of the settings option.
  keyid: string;

  // Label to be displayed in the UI
  label: string;

  // If "value" prop is null, this is next in line as the inital state value.
  defaultValue?: OptionsValueType;

  // Determines whether or not the grouping for "General" will be applied
  // The options that fall under the general group are the default & custom title.
  useGrouping?: boolean;

  // Widget specific title options
  widgetTitleOptions?: OptionsValueType[];

  // Current selected widget title value
  widgetTitleValue: string;

  // Show Query as an option
  showQueryOption?: boolean;
}

export default function SettingsWidgetTitleSelect(props: SettingsWidgetTitleSelectProps) {
  const { i18n } = useTranslation();
  const settignsOptions = useContext(SettingsOptionsContext);
  const useGrouping = props.useGrouping ?? true;

  const widgetTitleOptions = useMemo(() => {
    let opts = [
      { label: i18n.t("widget-settings.default-title"), value: 'default', group: 'General' } as OptionsValueType,
      { label: i18n.t("widget-settings.custom-title"), value: 'custom', group: 'General' } as OptionsValueType,
      { label: i18n.t("none"), value: 'none', group: 'General' } as OptionsValueType
    ];
    if (props.widgetTitleOptions) {
      opts = [...opts, ...props.widgetTitleOptions];
    }
    if (props.showQueryOption) {
      opts = [
        ...opts,
        { label: i18n.t("query"), value: 'query', group: 'Widget' } as OptionsValueType,
        { label: `${i18n.t("widget-settings.default-title")} - ${i18n.t("query")}`, value: 'query+title', group: 'Widget' } as OptionsValueType
      ];
    }
    return opts;
  }, [i18n, props.widgetTitleOptions, props.showQueryOption]);

  const GroupHeader = styled('div')(({ theme }) => ({
    padding: '4px 10px',
    backgroundColor: theme.palette.background.default
  }));
  const GroupItems = styled('ul')({ padding: 0 });

  const selectedValue = widgetTitleOptions.find((item: OptionsValueType) => (
    item.value === props.widgetTitleValue
  ));

  return (
    <Stack direction="row" sx={{
      alignItems: "center"
    }}>
      <Typography sx={{ flex: 1 }}>{props.label}</Typography>
      <Box sx={{ flex: 2 }}>
        <Autocomplete
          disableClearable
          options={widgetTitleOptions}
          getOptionLabel={(option: OptionsValueType) => option.label}
          groupBy={option => option?.group || ''}
          value={selectedValue}
          renderInput={(params) => <TextField autoComplete="off" {...params} />}
          renderGroup={(params) => (
            <li key={params.key}>
              {useGrouping && <GroupHeader>{params.group}</GroupHeader>}
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
          onChange={(e, newValue: OptionsValueType) => {
            settignsOptions?.updateSettingsState(props.keyid, newValue.value);
          }}
          slotProps={{
            popper: { className: "drag-cancel" }
          }}
        />
      </Box>
    </Stack>
  );
}